import { AlertTriangle, Download, Globe, Heart, Lock, Pause, Pencil, Play, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlayer } from "../context/PlayerContext";
import { downloadUrl } from "../lib/api";
import { STATUS } from "../lib/presets";
import { Cover, EqBars } from "./Cover";
import { AddToPlaylist } from "./AddToPlaylist";

const Progress = ({ status, phase }) => (
  <div className="mt-2 w-full max-w-xs">
    <div className="h-1.5 rounded-full bg-[var(--c-elev)] overflow-hidden">
      <div className="h-full shimmer rounded-full transition-[width] duration-700" style={{ width: `${STATUS[status]?.pct || 5}%` }} />
    </div>
    <p className="text-[11px] mt-1 text-[var(--c-violet)] font-medium">{STATUS[status]?.label}…{phase && <span className="text-zinc-500 font-mono ml-1">{phase}</span>}</p>
  </div>
);

const Act = ({ testId, onClick, children, active, danger }) => (
  <button data-testid={testId} onClick={onClick}
    className={`p-2 rounded-lg flex items-center gap-1 transition-colors duration-200 hover:bg-[var(--c-hover)] ${active ? "text-[var(--c-orange)]" : danger ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-white"}`}>
    {children}
  </button>
);

const RowThumb = ({ song, list, done, failed }) => {
  const { current, playing, play } = usePlayer();
  const on = current?.id === song.id && playing;
  return (
    <button data-testid={`song-row-play-${song.id}`} disabled={!done} onClick={() => play(song, list)}
      className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0 group">
      {done ? <Cover song={song} className="w-full h-full" /> : <div className={`w-full h-full ${failed ? "bg-red-950/40" : "shimmer"}`} />}
      {done && <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
        {on ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6" fill="currentColor" />}
      </span>}
    </button>
  );
};

const RowInfo = ({ song, done, failed, active, playing }) => (
  <div className="flex-1 min-w-0">
    <Link to={`/daina/${song.id}`} data-testid={`song-row-link-${song.id}`} className="font-semibold truncate flex items-center gap-2 hover:text-[var(--c-orange)] transition-colors">
      {active && playing && <EqBars />}{song.title || "Kuriama daina…"}
      {song.variant > 1 && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[var(--c-violet)]/20 text-[var(--c-violet)]">v{song.variant}</span>}
    </Link>
    <p className="text-xs text-zinc-500 truncate">{song.style || song.description}</p>
    {!done && !failed && <Progress status={song.status} phase={song.phase} />}
    {failed && <p data-testid={`song-row-error-${song.id}`} className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{song.error}</p>}
  </div>
);

const linkCls = "p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-[var(--c-hover)] transition-colors";

const OwnerActions = ({ song, onRename, onTogglePublic }) => (
  <>
    {onRename && <Act testId={`song-row-rename-${song.id}`} onClick={() => onRename(song)}><Pencil className="w-4 h-4" /></Act>}
    {onTogglePublic && <Act testId={`song-row-visibility-${song.id}`} onClick={() => onTogglePublic(song)}>{song.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</Act>}
  </>
);

const DoneActions = ({ song, onLike, onRename, onTogglePublic }) => (
  <>
    {onLike && <Act testId={`song-row-like-${song.id}`} onClick={() => onLike(song)} active={song.liked}><Heart className="w-4 h-4" fill={song.liked ? "currentColor" : "none"} /><span data-testid={`song-row-likes-${song.id}`} className="text-xs font-mono">{song.likes}</span></Act>}
    {song.is_mine && <OwnerActions song={song} onRename={onRename} onTogglePublic={onTogglePublic} />}
    <AddToPlaylist song={song} className={linkCls} />
    <a data-testid={`song-row-download-${song.id}`} href={downloadUrl(song)} className={linkCls}><Download className="w-4 h-4" /></a>
  </>
);

export const SongRow = ({ song, list, onLike, onDelete, onTogglePublic, onRename }) => {
  const { current, playing } = usePlayer();
  const active = current?.id === song.id;
  const done = song.status === "completed";
  const failed = song.status === "failed";
  return (
    <div data-testid={`song-row-${song.id}`}
      className={`flex items-center gap-4 p-3 rounded-2xl border transition-colors duration-200 rise ${active ? "border-[var(--c-orange)]/50 bg-[var(--c-elev)]" : "border-transparent hover:bg-[var(--c-surface)]"}`}>
      <RowThumb song={song} list={list} done={done} failed={failed} />
      <RowInfo song={song} done={done} failed={failed} active={active} playing={playing} />
      <div className="flex items-center">
        {done && <DoneActions song={song} onLike={onLike} onRename={onRename} onTogglePublic={onTogglePublic} />}
        {onDelete && song.is_mine && <Act testId={`song-row-delete-${song.id}`} onClick={() => onDelete(song)} danger><Trash2 className="w-4 h-4" /></Act>}
      </div>
    </div>
  );
};
